/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.0
 * Description: KT AI Assistant chatbot interface with database access
 */

import React, { useMemo, useState, useEffect, useRef } from 'react'
import {
  Box,
  Container,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  Button,
  InputAdornment,
} from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import SendIcon from '@mui/icons-material/Send'
import PersonIcon from '@mui/icons-material/Person'
import AddCommentIcon from '@mui/icons-material/AddComment'
import SearchIcon from '@mui/icons-material/Search'
import { api, ChatMessage, ChatThread } from '@services/api'
import { useUserContext } from '@context/UserContext'
import { useTheme } from '@context/ThemeContext'

const ChatBot: React.FC = () => {
  const { activeContext, switchContext } = useUserContext()
  const { resolvedTheme } = useTheme()
  const muiTheme = useMemo(() => createTheme({ palette: { mode: resolvedTheme } }), [resolvedTheme])
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [chatAvailable, setChatAvailable] = useState<boolean | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const conversationGenerationRef = useRef(0)
  const previousContextIdRef = useRef(activeContext?.id)
  const pendingContextSwitchRef = useRef<number | null>(null)

  // Saved chat threads — sidebar list, search, and the currently open thread
  const [threads, setThreads] = useState<ChatThread[]>([])
  const [threadSearch, setThreadSearch] = useState('')
  const [currentThreadId, setCurrentThreadId] = useState<number | null>(null)
  const [currentThreadContextId, setCurrentThreadContextId] = useState<number | null>(null)
  const [currentThreadTitle, setCurrentThreadTitle] = useState<string | null>(null)

  useEffect(() => {
    checkChatStatus()
  }, [])

  useEffect(() => {
    if (chatAvailable) {
      loadThreads()
    }
  }, [chatAvailable])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    const contextId = activeContext?.id
    if (previousContextIdRef.current === contextId) return

    previousContextIdRef.current = contextId
    const isSelectedThreadContext = pendingContextSwitchRef.current === contextId
    pendingContextSwitchRef.current = null
    if (!isSelectedThreadContext) {
      conversationGenerationRef.current += 1
      setLoading(false)
    }
    setCurrentThreadId(null)
    setCurrentThreadContextId(null)
    setCurrentThreadTitle(null)
    setMessages([])
    setInput('')
    setError(null)
  }, [activeContext?.id])

  const checkChatStatus = async () => {
    try {
      const status = await api.getChatStatus()
      setChatAvailable(status.available)

      if (status.available) {
        // Add welcome message
        setMessages([
          {
            role: 'assistant',
            content:
              "Hello! I'm your Systems IQ AIBot! I have access to all your DAL-ify systems and data. How can I help you today?\n\nYou can ask me to:\n- Query data from any table\n- Analyze trends and patterns\n- Generate insights and reports\n- Answer questions about your database structure\n- Help manage and update data",
          },
        ])
      } else {
        setError(status.message)
      }
    } catch (err) {
      setError('Failed to connect to chatbot service')
      setChatAvailable(false)
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadThreads = async (search?: string) => {
    try {
      setThreads(await api.listChatThreads(search))
    } catch (err) {
      console.error('Failed to load chat threads:', err)
    }
  }

  const handleThreadSearchChange = (value: string) => {
    setThreadSearch(value)
    loadThreads(value)
  }

  const handleNewChat = () => {
    conversationGenerationRef.current += 1
    pendingContextSwitchRef.current = null
    setLoading(false)
    setCurrentThreadId(null)
    setCurrentThreadContextId(null)
    setCurrentThreadTitle(null)
    setMessages([])
    setInput('')
    setError(null)
  }

  const handleSelectThread = async (thread: ChatThread) => {
    const requestGeneration = ++conversationGenerationRef.current
    setLoading(true)
    setError(null)
    try {
      // The thread may belong to a different context than the one currently active —
      // switch first so the header on getChatThread matches what the thread expects.
      if (thread.context_id !== activeContext?.id) {
        pendingContextSwitchRef.current = thread.context_id
      }
      switchContext(thread.context_id)
      const { messages: threadMessages } = await api.getChatThread(thread.id)
      if (requestGeneration !== conversationGenerationRef.current) return
      setCurrentThreadId(thread.id)
      setCurrentThreadContextId(thread.context_id)
      setCurrentThreadTitle(thread.title)
      setMessages(
        threadMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
      )
    } catch (err) {
      if (requestGeneration !== conversationGenerationRef.current) return
      setError('Failed to load that chat thread')
      console.error('Failed to load chat thread:', err)
    } finally {
      if (requestGeneration === conversationGenerationRef.current) {
        setLoading(false)
      }
    }
  }

  const handleSend = async () => {
    if (!input.trim() || loading) {
      return
    }

    const userMessage: ChatMessage = {
      role: 'user',
      content: input.trim(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput('')
    setLoading(true)
    setError(null)
    const requestGeneration = conversationGenerationRef.current

    try {
      let threadId = currentThreadContextId === activeContext?.id ? currentThreadId : null
      if (threadId === null) {
        const title = userMessage.content.slice(0, 60)
        const thread = await api.createChatThread(title)
        if (requestGeneration !== conversationGenerationRef.current) return
        threadId = thread.id
        setCurrentThreadId(threadId)
        setCurrentThreadContextId(thread.context_id)
        setCurrentThreadTitle(thread.title)
      }

      const { assistantMessage } = await api.sendThreadMessage(threadId, userMessage.content)
      if (requestGeneration !== conversationGenerationRef.current) return

      setMessages((prev) => [...prev, { role: 'assistant', content: assistantMessage.content }])
      loadThreads(threadSearch)
    } catch (err: any) {
      if (requestGeneration !== conversationGenerationRef.current) return
      // Roll back the optimistic user message — otherwise it stays in history and
      // gets resent with every later request, so a deterministic backend failure
      // (e.g. a bad prompt) permanently breaks the conversation.
      setMessages((prev) => prev.filter((m) => m !== userMessage))
      setInput(userMessage.content)
      setError(err.response?.data?.error || 'Failed to send message')
      console.error('Chat error:', err)
    } finally {
      if (requestGeneration === conversationGenerationRef.current) {
        setLoading(false)
      }
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const suggestedQuestions = [
    'How many systems are in the database?',
    'Show me all systems with high security scores',
    'What products are most commonly used?',
    'Analyze system acquisition trends',
  ]

  const handleSuggestionClick = (question: string) => {
    setInput(question)
  }

  if (chatAvailable === null) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Container>
      </ThemeProvider>
    )
  }

  if (!chatAvailable) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">{error || 'Chatbot is not available. Please contact your administrator.'}</Alert>
        </Container>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 2, height: 'calc(100vh - 120px)', display: 'flex', gap: 2 }}>
        {/* Saved chat threads \u2014 search, resume, or start a new one */}
        <Paper
          elevation={2}
          sx={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<AddCommentIcon />}
              onClick={handleNewChat}
              sx={{ mb: 1 }}
            >
              New chat
            </Button>
            <TextField
              fullWidth
              size="small"
              placeholder="Search chats..."
              value={threadSearch}
              onChange={(e) => handleThreadSearchChange(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
          <List sx={{ flexGrow: 1, overflow: 'auto', py: 0 }}>
            {threads.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No saved chats yet
              </Typography>
            ) : (
              threads.map((thread) => (
                <ListItemButton
                  key={thread.id}
                  selected={thread.id === currentThreadId}
                  onClick={() => handleSelectThread(thread)}
                >
                  <ListItemText
                    primary={
                      <Typography noWrap variant="body2">
                        {thread.context_display_name ? `${thread.context_display_name}: ${thread.title}` : thread.title}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Paper>

        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <img src="/dolly.png" alt="Dolly the Sheep" style={{ width: 60, height: 60, borderRadius: '50%' }} />
            <Box>
              <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                KT AI Assistant
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentThreadTitle || 'Your Systems IQ AIBot'}
              </Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper
            elevation={3}
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Messages Area */}
            <Box
              sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 2,
                bgcolor: 'background.default',
              }}
            >
              {messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <img
                    src="/dolly.png"
                    alt="Dolly the Sheep"
                    style={{ width: 80, height: 80, borderRadius: '50%', marginBottom: 16 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    Start a conversation with "Dolly the Sheep"
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Try asking one of these questions:
                  </Typography>
                  <Stack direction="column" spacing={1} sx={{ alignItems: 'center' }}>
                    {suggestedQuestions.map((question, index) => (
                      <Chip
                        key={index}
                        label={question}
                        onClick={() => handleSuggestionClick(question)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : (
                messages.map((message, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', maxWidth: '70%', gap: 1 }}>
                      {message.role === 'assistant' && (
                        <Avatar src="/dolly.png" alt="Dolly the Sheep" sx={{ width: 40, height: 40 }} />
                      )}

                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          bgcolor: message.role === 'user' ? 'primary.light' : 'background.paper',
                          color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {message.content}
                        </Typography>
                      </Paper>

                      {message.role === 'user' && (
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <PersonIcon />
                        </Avatar>
                      )}
                    </Box>
                  </Box>
                ))
              )}

              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar src="/dolly.png" alt="Dolly the Sheep" sx={{ width: 40, height: 40 }} />
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <CircularProgress size={20} />
                    </Paper>
                  </Box>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input Area */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  placeholder="Ask me anything about your database..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  variant="outlined"
                  size="small"
                />
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                    '&:disabled': {
                      bgcolor: 'action.disabledBackground',
                    },
                  }}
                >
                  <SendIcon />
                </IconButton>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Press Enter to send, Shift+Enter for new line
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  )
}

export default ChatBot
